import { ErrorHandlingService } from './error-handling.service';
import { Injectable } from '@angular/core';
import { Observable, throwError, Subject, EMPTY } from 'rxjs';
import { Photo } from '../classes/photo';
import { PhotoId } from '../classes/photo-id';
import { ProNumber } from '../classes/pronumber';
import { IDBPhoto } from '../classes/idbphoto';
import { DmsOpCode } from '../enums/dms-op-code.enum';

export class IDBSpace {
  availableKB: number;
  usedKB: number;
  spaceLimitKB: number;

  constructor() {
    this.availableKB = 0;
    this.usedKB = 0;
    this.spaceLimitKB = 50000; // 50MB
  }
}

@Injectable({
  providedIn: 'root'
})
export class AppStorageService {
  private static DEFAULT_IDB_VERSION_NBR = 1; // This is the schema version number of the database, there shouldn't be a need to update this.
  private static DEFAULT_IDB_NAME = 'XPO';
  private static DEFAULT_IDB_WRITEPERMISSION = 'readwrite' as IDBTransactionMode;
  private static DEFAULT_IDB_READPERMISSION = 'readonly' as IDBTransactionMode;
  private static DEFAULT_IDB_STORE = 'inspection-photos';
  private static DEFAULT_IDB_STOREKEYPATH = 'photoId';
  private static DEFAULT_IDB_STATUS_INDEX_NAME = 'status_idx';
  private static DEFAULT_IDB_PRO_INDEX_NAME = 'pro_idx';

  private _indexedDB: IDBFactory;
  private _dbName: string;
  private _indexedDBSpaceSubject;
  private _photoCountSubject;

  constructor(private errorHandling: ErrorHandlingService) {
    this._indexedDB = window.indexedDB; // IndexedDB Object
    this._dbName = AppStorageService.DEFAULT_IDB_NAME; // IndexedDB Name
  }

  /*  Note:
   *  The initializeDB, CreateDB, and DeleteDB have been tested in various situations of upgraded versions
   *  Manual IndexedDB deletes, deletes while the application is running, and so forth
   *  Do not change unless you know all the impacts and are willing to spend a lot of time
   *  Running through all edge cases.
   **/

  public openDb(): Observable<IDBDatabase> {
    return new Observable((observer: any) => {
      const openRequest = this._indexedDB.open(this._dbName, AppStorageService.DEFAULT_IDB_VERSION_NBR);

      let upgradeNeeded = false;
      // onupgradeneeded could happen after the onsuccess, but in all testing it doesn't appear to
      // use upgrade needed in onsuccess since it's called anyway
      openRequest.onupgradeneeded = () => {
        console.log('InitializeDB: Upgrade is needed!');
        upgradeNeeded = true;
      };

      openRequest.onsuccess = (resp) => {
        const db = openRequest.result;
        if (!db.objectStoreNames.contains(AppStorageService.DEFAULT_IDB_STORE)) {
          upgradeNeeded = true;
        } else {
          try {
            const tx = db.transaction(
              AppStorageService.DEFAULT_IDB_STORE,
              AppStorageService.DEFAULT_IDB_READPERMISSION
            );
            const store = tx.objectStore(AppStorageService.DEFAULT_IDB_STORE);
            store.index(AppStorageService.DEFAULT_IDB_STATUS_INDEX_NAME);
            store.index(AppStorageService.DEFAULT_IDB_PRO_INDEX_NAME);
          } catch (error) {
            console.log('Missing index(es). Rebuilding Database!');
            upgradeNeeded = true;
          }
        }

        if (upgradeNeeded) {
          this.close(db);
          this.createDB().subscribe(() => {
            observer.next(true);
            observer.complete();
          });
        } else {
          observer.next(db);
          observer.complete();
        }
      };

      openRequest.onerror = (error) => {
        observer.error('Error Initializing IndexedDB: ' + openRequest.error.toString());
        this.handleError('Error Initializing IndexedDB: ' + openRequest.error.toString());
      };
    });
  }

  public createDB(): Observable<boolean> {
    console.log('Creating IndexedDB');
    return new Observable((observer: any) => {
      this.deleteDB().subscribe((response) => {
        const openRequest = this._indexedDB.open(this._dbName, AppStorageService.DEFAULT_IDB_VERSION_NBR);

        openRequest.onupgradeneeded = () => {
          const db = openRequest.result;
          const store = db.createObjectStore(AppStorageService.DEFAULT_IDB_STORE, {
            keyPath: AppStorageService.DEFAULT_IDB_STOREKEYPATH
          });
          store.createIndex(AppStorageService.DEFAULT_IDB_STATUS_INDEX_NAME, 'status', { unique: false });
          store.createIndex(AppStorageService.DEFAULT_IDB_PRO_INDEX_NAME, 'proNumber', {
            unique: false
          });
        };

        openRequest.onsuccess = () => {
          const db = openRequest.result;
          this.close(db);
          observer.next(true);
          observer.complete();
        };

        openRequest.onerror = (error) => {
          observer.error('Error Initializing IndexedDB: ' + openRequest.error.toString());
          this.handleError('Error Initializing IndexedDB: ' + openRequest.error.toString());
        };
      });
    });
  }

  public deleteDB(): Observable<boolean> {
    return new Observable((observer: any) => {
      const request = this._indexedDB.deleteDatabase(this._dbName);

      request.onsuccess = () => {
        observer.next(true);
        observer.complete();
      };

      request.onerror = () => {
        observer.error('Error Deleting IndexedDB: ' + request.error.toString());
        this.handleError('Error Deleting IndexedDB: ' + request.error.toString());
      };

      request.onblocked = () => {
        if (request) {
          const db = request.result;
          this.close(db);
        }
        observer.error('Error Deleting IndexedDB: ' + request.error.toString());
        this.handleError('Error Deleting IndexedDB: ' + request.error.toString());
      };

      request.onerror = (error) => {
        observer.error('Error Deleting IndexedDB: ' + request.error.toString());
        this.handleError('Error Deleting IndexedDB: ' + request.error.toString());
      };
    });
  }

  private handleError(msg: string) {
    console.error(msg);
    this.errorHandling.showErrorMessage(this.errorHandling.buildGeneralErrorMessage('ERROR IN STORAGE SERVICE: ', msg));
    return EMPTY;
  }

  private close(dbObject) {
    if (dbObject) {
      dbObject.close();
    }
  }

  // if item with same key exists, fails with constraint error
  public insertPhoto(photo: Photo): Observable<boolean> {
    return new Observable((observer: any) => {
      this.openDb().subscribe((db: IDBDatabase) => {
        const tx = db.transaction(
          AppStorageService.DEFAULT_IDB_STORE,
          AppStorageService.DEFAULT_IDB_WRITEPERMISSION
        ) as IDBTransaction;

        const store = tx.objectStore(AppStorageService.DEFAULT_IDB_STORE) as IDBObjectStore;

        const idbPhoto = IDBPhoto.toIDBPhoto(photo);

        const request = store.add(idbPhoto);

        tx.oncomplete = (e: any) => {
          observer.next(true);
          observer.complete();
          this.close(db);
        };

        request.onerror = (e: any) => {
          observer.error('IndexedDB insertPhoto error: ' + e.target.error);
          this.handleError('IndexedDB insertPhoto error: ' + e.target.error);
          this.close(db);
        };
        tx.onerror = (e: any) => {
          observer.error('IndexedDB insertPhoto error: ' + e.target.error);
          this.handleError('IndexedDB insertPhoto error: ' + e.target.error);
          this.close(db);
        };

        db.onerror = (e: any) => {
          observer.error('IndexedDB insertPhoto error: ' + e.target.error);
          this.handleError('IndexedDB insertPhoto error: ' + e.target.error);
          this.close(db);
        };
        this.calcDBSizeAndPhotoCount();
      });
    });
  }

  public getPhoto(photoId: string): Observable<Photo> {
    return new Observable((observer: any) => {
      this.openDb().subscribe((db: any) => {
        const tx = db.transaction(AppStorageService.DEFAULT_IDB_STORE, AppStorageService.DEFAULT_IDB_READPERMISSION);
        const store = tx.objectStore(AppStorageService.DEFAULT_IDB_STORE);
        const request = store.get(photoId);

        request.onsuccess = (event) => {
          const photo = this.buildPhoto(event.target.result);
          observer.next(photo);
          observer.complete();
          this.close(db);
        };

        request.onerror = (e: any) => {
          observer.error('IndexedDB getPhoto error: ' + e.target.error);
          this.handleError('IndexedDB getPhoto error: ' + e.target.error);
          this.close(db);
        };

        db.onerror = (e: any) => {
          observer.error('IndexedDB getPhoto error: ' + e.target.error);
          this.handleError('IndexedDB getPhoto error: ' + e.target.error);
          this.close(db);
        };
      });
    });
  }

  public deletePhotosByProAndStatusCd(proNumber: ProNumber, statusCd: string): Observable<boolean> {
    return new Observable((observer: any) => {
      this.openDb().subscribe((db: any) => {
        const tx = db.transaction(AppStorageService.DEFAULT_IDB_STORE, AppStorageService.DEFAULT_IDB_WRITEPERMISSION);
        const store = tx.objectStore(AppStorageService.DEFAULT_IDB_STORE);

        const index = store.index(AppStorageService.DEFAULT_IDB_PRO_INDEX_NAME);
        const cursor = index.openCursor(IDBKeyRange.only(proNumber.formatProNumber()));

        cursor.onsuccess = (event) => {
          const cursorResult = event.target.result;
          if (cursorResult) {
            if (cursorResult.value.status === statusCd) {
              store.delete(cursorResult.primaryKey);
              cursorResult.continue();
            }
          }
        };

        tx.oncomplete = (event) => {
          observer.next(true);
          observer.complete();
          this.close(db);
        };

        cursor.onerror = (e: any) => {
          observer.error('IndexedDB deletePhotosByPro error: ' + e.target.error);
          this.handleError('IndexedDB deletePhotosByPro error: ' + e.target.error);
          this.close(db);
        };

        db.onerror = (e: any) => {
          observer.error('IndexedDB deletePhotosByPro error: ' + e.target.error);
          this.handleError('IndexedDB deletePhotosByPro error: ' + e.target.error);
          this.close(db);
        };
        this.calcDBSizeAndPhotoCount();
      });
    });
  }

  public deletePhoto(id: string): Observable<boolean> {
    return new Observable((observer: any) => {
      this.openDb().subscribe((db: any) => {
        const tx = db.transaction(AppStorageService.DEFAULT_IDB_STORE, AppStorageService.DEFAULT_IDB_WRITEPERMISSION);
        const store = tx.objectStore(AppStorageService.DEFAULT_IDB_STORE);

        const request = store.delete(id);

        tx.oncomplete = (event) => {
          observer.next(true);
          observer.complete();
          this.close(db);
        };

        request.onerror = (e: any) => {
          observer.error('IndexedDB deletePhoto error: ' + e.target.error);
          this.handleError('IndexedDB deletePhoto error: ' + e.target.error);
          this.close(db);
        };
        tx.onerror = (e: any) => {
          observer.error('IndexedDB deletePhoto error: ' + e.target.error);
          this.handleError('IndexedDB deletePhoto error: ' + e.target.error);
          this.close(db);
        };

        db.onerror = (e: any) => {
          observer.error('IndexedDB deletePhoto error: ' + e.target.error);
          this.handleError('IndexedDB deletePhoto error: ' + e.target.error);
          this.close(db);
        };
        this.calcDBSizeAndPhotoCount();
      });
    });
  }

  private getPhotoCount(): Observable<number> {
    return new Observable((observer: any) => {
      this.openDb().subscribe((db: any) => {
        const tx = db.transaction(
          AppStorageService.DEFAULT_IDB_STORE,
          AppStorageService.DEFAULT_IDB_READPERMISSION
        ) as IDBTransaction;
        const store = tx.objectStore(AppStorageService.DEFAULT_IDB_STORE) as IDBObjectStore;
        const request = store.count() as IDBRequest<number>;

        request.onsuccess = (event) => {
          observer.next(event.target['result']);
          observer.complete();
          this.close(db);
        };

        request.onerror = (e: any) => {
          observer.error('IndexedDB getPhotoCount error: ' + e.target.error);
          this.handleError('IndexedDB getPhotoCount error: ' + e.target.error);
          this.close(db);
        };

        db.onerror = (e: any) => {
          observer.error('IndexedDB getPhotoCount error: ' + e.target.error);
          this.handleError('IndexedDB getPhotoCount error: ' + e.target.error);
          this.close(db);
        };
      });
    });
  }

  // if no status is provided, will grab all status
  public getPhotosByStatus(status?: string): Observable<Photo[]> {
    return new Observable((observer: any) => {
      this.openDb().subscribe((db: any) => {
        const tx = db?.transaction(AppStorageService.DEFAULT_IDB_STORE, AppStorageService.DEFAULT_IDB_READPERMISSION);
        const store = tx.objectStore(AppStorageService.DEFAULT_IDB_STORE);
        const index = store.index(AppStorageService.DEFAULT_IDB_STATUS_INDEX_NAME);
        const cursor = index.openCursor(status);

        const photos: Photo[] = [];
        cursor.onsuccess = (event) => {
          const cursorResult = event.target.result;
          if (cursorResult) {
            const photo = this.buildPhoto(cursorResult.value);
            photos.push(photo);
            cursorResult.continue();
          } else {
            observer.next(photos);
            observer.complete();
            this.close(db);
          }
        };

        cursor.onerror = (e: any) => {
          observer.error('IndexedDB getPhotosByStatus error: ' + e.target.error);
          this.handleError('IndexedDB getPhotosByStatus error: ' + e.target.error);
          this.close(db);
        };

        db.onerror = (e: any) => {
          observer.error('IndexedDB getPhotosByStatus error: ' + e.target.error);
          this.handleError('IndexedDB getPhotosByStatus error: ' + e.target.error);
          this.close(db);
        };
      });
    });
  }

  public setPhotoStatusByPro(proNumber: ProNumber, status: string): Observable<boolean> {
    return new Observable<boolean>((observer: any) => {
      this.openDb().subscribe((db: any) => {
        const tx = db.transaction(AppStorageService.DEFAULT_IDB_STORE, AppStorageService.DEFAULT_IDB_WRITEPERMISSION);
        const store = tx.objectStore(AppStorageService.DEFAULT_IDB_STORE);
        const index = store.index(AppStorageService.DEFAULT_IDB_PRO_INDEX_NAME);
        const cursor = index.openCursor(proNumber.formatProNumber());

        cursor.onsuccess = (event) => {
          const cursorResult = event.target.result;
          if (cursorResult) {
            const idbPhoto: IDBPhoto = cursorResult.value;
            idbPhoto.status = status;
            store.put(idbPhoto);
            cursorResult.continue();
          }
        };

        cursor.onerror = (e: any) => {
          observer.error('IndexedDB setPhotoStatusByPro error: ' + e.target.error);
          this.handleError('IndexedDB setPhotoStatusByPro error: ' + e.target.error);
          this.close(db);
        };

        tx.oncomplete = (event) => {
          observer.next(true);
          observer.complete();
          this.close(db);
        };

        tx.onerror = (e: any) => {
          observer.error('IndexedDB setPhotoStatusByPro error: ' + e.target.error);
          this.handleError('IndexedDB setPhotoStatusByPro error: ' + e.target.error);
          this.close(db);
        };

        db.onerror = (e: any) => {
          observer.error('IndexedDB setPhotoStatusByPro error: ' + e.target.error);
          this.handleError('IndexedDB setPhotoStatusByPro error: ' + e.target.error);
          this.close(db);
        };
      });
    });
  }

  public getPhotoCountByProAndStatus(proNumber: ProNumber, status: String): Observable<number> {
    return new Observable<number>((observer: any) => {
      this.openDb().subscribe((db: any) => {
        const tx = db.transaction(AppStorageService.DEFAULT_IDB_STORE, AppStorageService.DEFAULT_IDB_READPERMISSION);
        const store = tx.objectStore(AppStorageService.DEFAULT_IDB_STORE);
        const index = store.index(AppStorageService.DEFAULT_IDB_PRO_INDEX_NAME);
        const cursor = index.openCursor(proNumber.formatProNumber());

        let count = 0;
        cursor.onsuccess = (event) => {
          const cursorResult = event.target.result;
          if (cursorResult) {
            const idbPhoto: IDBPhoto = cursorResult.value;
            if (idbPhoto.status === status) {
              count++;
            }
            cursorResult.continue();
          } else {
            observer.next(count);
            observer.complete();
            this.close(db);
          }
        };

        cursor.onerror = (e: any) => {
          observer.error('IndexedDB getPhotoCountByProAndStatus error: ' + e.target.error);
          this.handleError('IndexedDB getPhotoCountByProAndStatus error: ' + e.target.error);
          this.close(db);
        };

        db.onerror = (e: any) => {
          observer.error('IndexedDB getPhotoCountByProAndStatus error: ' + e.target.error);
          this.handleError('IndexedDB getPhotoCountByProAndStatus error: ' + e.target.error);
          this.close(db);
        };
      });
    });
  }

  public getPhotosByPro(proNumber: ProNumber): Observable<Photo[]> {
    return new Observable((observer: any) => {
      this.openDb().subscribe((db: any) => {
        const tx = db.transaction(AppStorageService.DEFAULT_IDB_STORE, AppStorageService.DEFAULT_IDB_READPERMISSION);
        const store = tx.objectStore(AppStorageService.DEFAULT_IDB_STORE);

        const index = store.index(AppStorageService.DEFAULT_IDB_PRO_INDEX_NAME);
        const cursor = index.openCursor(proNumber.formatProNumber());

        const photos: Photo[] = [];

        cursor.onsuccess = (event) => {
          const cursorResult = event.target.result;
          if (cursorResult) {
            const photo = this.buildPhoto(cursorResult.value);
            photos.push(photo);
            cursorResult.continue();
          } else {
            observer.next(photos);
            observer.complete();
            this.close(db);
          }
        };

        cursor.onerror = (e: any) => {
          observer.error('IndexedDB getPhotosByPro error: ' + e.target.error);
          this.handleError('IndexedDB getPhotosByPro error: ' + e.target.error);
          this.close(db);
        };

        db.onerror = (e: any) => {
          observer.error('IndexedDB getPhotosByPro error: ' + e.target.error);
          this.handleError('IndexedDB getPhotosByPro error: ' + e.target.error);
          this.close(db);
        };
      });
    });
  }

  private buildPhoto(idbPhoto: IDBPhoto): Photo {
    const photoId = new PhotoId(new ProNumber(idbPhoto.proNumber), idbPhoto.imageType, idbPhoto.photoId);

    const photo = new Photo(photoId);

    photo.setBase64dataAndContentType(idbPhoto.photoBytes, idbPhoto.photoContentType);
    photo.fileName = idbPhoto.fileName ? idbPhoto.fileName : '';
    photo.dmsOpCode = idbPhoto.status;
    photo.createDate = idbPhoto.createDate;
    photo.photoSizeKB = idbPhoto.photoSizeKB;
    if (idbPhoto.imgSrcForHtml) {
      photo.imgSrcForHtml = idbPhoto.imgSrcForHtml;
    }

    return photo;
  }
  public getIndexedDBSpace(): Observable<IDBSpace> {
    return new Observable<IDBSpace>((observer: any) => {
      const idbSpace = new IDBSpace();
      this.getPhotosByStatus(DmsOpCode.NEWLY_ADDED_PHOTO).subscribe((photos: Photo[]) => {
        let usedSpace = 0;
        photos.forEach((photo) => {
          usedSpace += photo.photoSizeKB;
        });
        idbSpace.usedKB = usedSpace;
        idbSpace.availableKB = parseFloat((idbSpace.spaceLimitKB - usedSpace / 1000).toFixed(2));
        observer.next(idbSpace);
        observer.complete();
      });
    });
  }
  public getAvailableSpace(): Observable<IDBSpace> {
    if (!this._indexedDBSpaceSubject) {
      this._indexedDBSpaceSubject = new Subject<IDBSpace>();
      this.calcDBSizeAndPhotoCount();
    }
    return this._indexedDBSpaceSubject.asObservable();
  }
  private calcDBSizeAndPhotoCount(): void {
    const idbSpace = new IDBSpace();
    this.getPhotosByStatus().subscribe((photos: Photo[]) => {
      let usedSpace = 0;
      photos.forEach((photo) => {
        usedSpace += photo.photoSizeKB;
      });
      idbSpace.usedKB = usedSpace;
      idbSpace.availableKB = parseFloat((idbSpace.spaceLimitKB - usedSpace / 1000).toFixed(2));

      this._indexedDBSpaceSubject.next(idbSpace);
    });
    this.getPhotoCount().subscribe((photoCount: number) => {
      this._photoCountSubject.next(photoCount);
    });
  }

  // review public and private method
  public getStoredPhotosCount(): Observable<number> {
    if (!this._photoCountSubject) {
      this._photoCountSubject = new Subject<number>();
      this.calcDBSizeAndPhotoCount();
    }
    return this._photoCountSubject.asObservable();
  }
}
